import {h} from 'preact';
import {useContext, createContext, useState} from "preact/compat";
import {useEffect} from "preact/hooks";

// Creates a new empty context
const GtmContext = createContext(null);

// This is the GTM context in which we will store data layer.
export const GtmContextProvider = props => {
    // Destructured props
    const {children} = props;

    // State to store all the products inside the cart
    // already formatted to the desired data model.
    const [dataLayerProducts, setDataLayerProducts] = useState([]);

    // method to validate GTM module
    const validateGTMTagScript = () => {
        try {
            const scriptTag = document.querySelector('#GTM_data');

            if (!scriptTag) {
                throw new Error("GTM_data script is not loaded");
            }
            
            const {isEnabled} = JSON.parse(scriptTag.textContent);

            if (!isEnabled) {
                throw new Error('GTM desabilitado');
            }

            return true;

        } catch ({message}) {
            // Otherwise if the operation fails, then it throws an error to the console
            console.log("GTM Script:", message);
            return false;
        }
    }

    // method to send GTM event
    const addEventToGTM = (event) => {
        try {
            if (!validateGTMTagScript()) {
                return;
            }

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(event);

        } catch ({message}) {
            // Otherwise if the operation fails, then it throws an error to the console
            console.error("Error while save GTM event:", message);
        }
    }

    const formatStringToNumber = (formattedString) => {
        if(!formattedString || formattedString === '') {
            return 0;
        }
        
        // Remove thousands separators
        let withoutThousandSeparator = formattedString.replace(/\./g, '');

        // Replace comma with a dot
        let withDot = withoutThousandSeparator.replace(',', '.');

        // Convert to float
        return parseFloat(withDot);
    };

    // handler to prepare and send addProduct event
    const handleAddProductEvent = (product) => {
        const productDetailsEvent = {
            event: 'eec.addCart',
            ecommerce: {
                add: {
                    products: [{
                        id: product.id || null,
                        name: product.name || null,
                        category: product.category || null,
                        variant: product.variant || null,
                        brand: product.brand || null,
                        quantity: product.quantity || null,
                        price: formatStringToNumber(product.price),
                    }]
                }
            }
        };

        addEventToGTM(productDetailsEvent);
    }

    // handler to prepare and send impressionClick event
    const handleImpressionClick = (data) => {
        const eventPayload = {
            'event': 'impressionClick', 
            'eventCallback': data?.product?.href || "",
            'ecommerce': {
                'click': {
                    'actionField': {
                        'list': data.list
                    },
                    'products': [{
                        'id': data.product.id,
                        'name': data.product.name,
                        'category': data.product.categories,
                        'position': 1
                    }]
                }
            }
        }

        addEventToGTM(eventPayload);
    }

    // handler to prepare and send refund event
    const handleRefundEvent = order => {
        const productList = order.items.products.map(product => {
            return {
                id: product.productId,
                name: product.name,
                qty: product.qty || 1,
            }
        });

        const eventPayload = {
            event: 'refund',
            ecommerce: {
                refund: {
                    actionField: {
                        id: order.incrementId
                    },
                    products: productList
                },
            }
        }

        addEventToGTM(eventPayload);
    };

    // handler to prepare and send productDetail event
    const handleProductDetailEvent = (data) => {
        const hasSpecialPrice = data.specialPrice && data.specialPrice !== "" && data.specialPrice !== "0,00";

        const price = hasSpecialPrice ? data.specialPrice : data.price;

        const productDetail = {
            id: data.id || null,
            name: data.name || null,
            category: data.category || null,
            price: formatStringToNumber(price),
        }

        const eventPayload = {
            event: 'productDetail',
            ecommerce: {
                detail: {
                    products: [productDetail]
                },
            },
        };
        addEventToGTM(eventPayload);
    };

    // handler to prepare and send impressionView event
    const handleImpressionViewEvent = (data, list, id) => {
        const impressions = data.map((item, index) => ({
            id: item.id || null,
            name: item.name || null, 
            category: item.category || null,
            position: index+1,
            list,
        }));

        const eventPayload = {
            event: 'impressionView',
            ecommerce: {
                impressions,
            },
            id,
        }
        addEventToGTM(eventPayload);
    };

    // Handles all the checkout steps events
    const handleDataLayerCheckoutStep = payload => {
        // Destructured props
        const {
            step,
            data = undefined,
            callback = undefined
        } = payload;

        if (
            (
                !data &&
                dataLayerProducts.length === 0
            ) ||
            (
                Array.isArray(data) &&
                data.length === 0
            )
        ) {
            return;
        }

        let layer = {
            event: "checkout",
            ecommerce: {
                checkout: {
                    actionField: {
                        step
                    },
                    products: data || [...dataLayerProducts]
                }
            }
        };

        if (typeof callback === "function") {
            layer['eventCallback'] = callback
        }

        addEventToGTM(layer);
    };

    const handleCheckoutOptionEvent = payload => {
        // Destructured props
        const {option} = payload;

        const layer = {
            event: "checkout_option",
            ecommerce: {
                checkout_option: {
                    actionField: {
                        step: 3,
                        option: option || ""
                    }
                }
            }
        };

        addEventToGTM(layer);
    }

    const prepareProduct = (product) => ({
        name: product["name"],
        id: product["productId"],
        price: formatStringToNumber(product["price"]),
        category: product["typeMarkdown"],
        quantity: product["qty"] || 1
    });

    const handlePurchaseEvent = payload => {
        const payloadProducts = payload?.items?.products;

        if (!payloadProducts) {
            return;
        }

        const products = payloadProducts.map(product => (prepareProduct(product)))

        payload.success ?
            handleCheckoutOptionEvent({
                option: payload["paymentType"]
            }) :
            handleRefundEvent(payload);

        const layer = {
            event: "purchase",
            ecommerce: {
                currencyCode: "BRL",
                purchase: {
                    actionField: {
                        id: payload["incrementId"],
                        affiliation: "Sabin",
                        value: formatStringToNumber(payload["total"]),
                        tax: "0",
                        shipping: payload["shipping"],
                        coupon: payload["coupon"],
                        email: payload["email"] || ""
                    },
                    products
                }
            }
        };

        addEventToGTM(layer);
    }

    // Method responsible for rehydrating the dataLayerProducts state when needed
    const hydrateDataLayerProductList = props => {
        // Destructured props
        const {data} = props;

        // Validates each case in which the operation shouldn't be performed
        if (
            !Array.isArray(data) ||
            (
                Array.isArray(data) &&
                data.length === 0
            )
        ) {
            return;
        }

        setDataLayerProducts([...data]);
    }

    /// --- FOR DEBUGGING PURPOSES ONLY ---
    // useEffect(() => {
    //     console.log("dataLayerProducts", dataLayerProducts);
    // }, [dataLayerProducts]);

    // The data that will be soon returned by the context
    const data = {
        addEventToGTM,
        handleImpressionViewEvent,
        handleProductDetailEvent,
        handleAddProductEvent,
        handleDataLayerCheckoutStep,
        handleCheckoutOptionEvent,
        handlePurchaseEvent,
        hydrateDataLayerProductList,
        handleImpressionClick,
        handleRefundEvent
    };

    return (
        <GtmContext.Provider value={data}>
            {children}
        </GtmContext.Provider>
    )
}

// Hook to provide a simple context accessibility
const useGtmContext = () => useContext(GtmContext);
export default useGtmContext;
